import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getSpecialsList } from '@/utils/nav';

const navItems = [
  { label: '首页', to: '/' },
  { label: '热点新闻', to: '/category/hot-news' },
  { label: '爆料报道', to: '/category/expose' },
  { label: '网络趣闻', to: '/category/web-fun' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const specials = getSpecialsList();

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => setMobileOpen(false)}>
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-xl" />
            </span>
            <span className="font-heading text-xl font-bold text-foreground-950 tracking-tight">51吃瓜网</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 rounded-md text-[15px] whitespace-nowrap transition-colors ${
                  isActive(item.to)
                    ? 'text-primary-600 font-medium bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setSpecialOpen(true)}
              onMouseLeave={() => setSpecialOpen(false)}
            >
              <button
                type="button"
                className={`px-4 py-2 rounded-md text-[15px] whitespace-nowrap transition-colors flex items-center gap-1 ${
                  location.pathname.startsWith('/special')
                    ? 'text-primary-600 font-medium bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                专题
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {specialOpen && (
                <div className="absolute left-0 top-full pt-2 w-64">
                  <div className="rounded-lg border border-background-200 bg-background-50 py-2 shadow-sm">
                    {specials.map((sp) => (
                      <Link
                        key={sp.id}
                        to={`/special/${sp.id}`}
                        onClick={() => setSpecialOpen(false)}
                        className="block px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        {sp.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-[15px] whitespace-nowrap transition-colors ${
                isActive('/about')
                  ? 'text-primary-600 font-medium bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </nav>

          <div className="hidden lg:flex items-center">
            <form onSubmit={submitSearch} className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索吃瓜资讯"
                className="w-56 pl-9 pr-3 py-2 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </form>
          </div>

          <div className="flex lg:hidden items-center gap-1">
            <Link
              to="/search"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label="菜单"
            >
              <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-4 space-y-1">
            <form onSubmit={submitSearch} className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索吃瓜资讯"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </form>
            {[...navItems, { label: '关于我们', to: '/about' }].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-md text-[15px] ${
                  isActive(item.to) ? 'bg-primary-50 text-primary-600 font-medium' : 'text-foreground-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-background-200 mt-2">
              <p className="px-4 py-2 text-xs text-foreground-400">热门专题</p>
              {specials.slice(0, 4).map((sp) => (
                <Link
                  key={sp.id}
                  to={`/special/${sp.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 text-sm text-foreground-700"
                >
                  {sp.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}